import "../AssetsTable.scss";
import { DFPEmpty } from "@/components/empty/dfp-empty";
import {
	type BaseTableAction,
	type BaseTableState,
	type SortOrder,
	baseInitialState,
	baseTableReducer,
} from "@/components/tables/shared-table-state";
import { DB_ORG } from "@/constants/db";
import { getAssetChildren, type ChildAssetRow } from "@/services/asset-service";
import { selfOrganizationAssetTypes } from "@/services/organization-service";
import { useUserStore } from "@/stores";
import {
	addSortOrderToColumns,
	createTableChangeHandler,
} from "@/utils/table-sort-utils";
import {
	getSessionValue,
	setSessionValue,
	SESSION_KEYS,
} from "@/utils/session-storage";
import {
	DownloadOutlined,
	PlusOutlined,
	ReloadOutlined,
	UploadOutlined,
} from "@ant-design/icons";
import { Pagination, Table } from "antd";
import { Button, Divider, Space, Tooltip } from "antd";
import { FC, useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DeleteButton } from "../../SubmissionsTable/DeleteButton";
import { DeleteModal } from "../../SubmissionsTable/DeleteModal";
import { DeletedSwitch } from "../../SubmissionsTable/DeletedSwitch";
import { clearAssetCache, fetchData } from "../childFunctions/fetchData";
import {
	getColumnsShown,
	initializeColumns,
} from "../childFunctions/initializeColumns";
import { AdminAssetDetail } from "./AdminAssetDetail";
import { initialColumns } from "./AssetsTableColumns";
import { ImportAssetsModal } from "./ImportAssetsModal";
import { SCExportDialog } from "./scExportDialog";
import {
	SmartFilter,
	type FilterCriteria,
	type FilterableColumn,
} from "@/layouts/signed-in/views/Locations/children/SmartFilter";

export interface Asset {
	id: string | number;
	organizationId: number;
	locationId: number;
	assetTag?: string;
	assetType?: {
		id: number;
		name: string;
	};
	assetTypeCategory?: {
		id: number;
		name: string;
	};
	location?: {
		id: number;
		name: string;
		address1?: string;
		address2?: string;
		city?: string;
		state?: string;
		zip?: string;
		country?: string;
	};
	manufacturer?: {
		id: number;
		name: string;
		isVerified: boolean;
		timeAdded: string;
	};
	modelNumber?: string;
	serialNumber?: string;
	manufacturerDate?: string;
	condition?: string;
	healthScore?: {
		healthScore?: number;
		replacementYear?: number;
		lastUpdate: string;
		remainingUsefulLife?: number;
	};
	warranty?: {
		hasWarranty?: boolean;
		isWarrantyExpired?: boolean;
		isNotFound?: boolean;
		startDate?: string;
		expirationDate?: string;
		type?: string;
		term?: number;
		provider?: string;
	};
	tempAlertTag?: string;
	submissionId: number;
	createdTimestamp: string;
	updatedTimestamp: string;
	/** Child asset hierarchy */
	parentAssetId?: number | null;
	tagType?: "PHYSICAL" | "VIRTUAL";
	childCount?: number;
	children?: Asset[];
}

interface AssetsTableProps {
	browserLocationId: string;
}
interface FilterOptions {
	assetTypes: string[];
}

export type Action =
	| {
			type: "SHOW_DECOMMISSIONED";
			payload: boolean | "ALL";
	  }
	| {
			type: "SET_DATE_RANGE";
			payload: [Date | null, Date | null];
	  }
	| {
			type: "SET_SHOW_DELETE_MODAL";
			payload: boolean;
	  }
	| {
			type: "SET_SHOW_SC_EXPORT";
			payload: boolean;
	  }
	| {
			type: "SET_VIEWING_DELETED";
			payload: boolean;
	  }
	| {
			type: "SET_FILTERS";
			payload: Record<string, any>;
	  }
	| {
			type: "SET_FILTER_OPTIONS";
			payload: FilterOptions | null;
	  }
	| {
			type: "SET_SELECTED_ASSET_ID";
			payload: string | null;
	  }
	| {
			type: "SET_SMART_FILTERS";
			payload: FilterCriteria[];
	  }
	| BaseTableAction;

// Initial state
export interface State extends BaseTableState {
	dateRange: [Date | null, Date | null];
	showDecommissioned: boolean | "ALL";
	showDeleteModal: boolean;
	showSCExport: boolean;
	viewingDeleted: boolean;
	filters: Record<string, any>;
	filterOptions: FilterOptions | null;
	selectedAssetId: string | null;
	smartFilters: FilterCriteria[];
}

// Load initial smart filters from session storage
const getInitialSmartFilters = (): FilterCriteria[] => {
	return getSessionValue<FilterCriteria[]>(SESSION_KEYS.ASSET_SMART_FILTERS, []);
};

const initialState: State = {
	...baseInitialState,
	sortBy: "assetTag",
	pageSize: 25,
	dateRange: [null, null],
	showDecommissioned: "ALL",
	showDeleteModal: false,
	showSCExport: false,
	viewingDeleted: false,
	filters: {},
	filterOptions: null,
	selectedAssetId: null,
	tableColumns: initialColumns,
	columnsShown: getColumnsShown(initialColumns),
	smartFilters: getInitialSmartFilters(),
};

function reducer(state: State, action: Action): State {
	switch (action.type) {
		case "SET_DATE_RANGE":
			return { ...state, dateRange: action.payload };
		case "SHOW_DECOMMISSIONED":
			return { ...state, showDecommissioned: action.payload };
		case "SET_SHOW_DELETE_MODAL":
			return { ...state, showDeleteModal: action.payload };
		case "SET_SHOW_SC_EXPORT":
			return { ...state, showSCExport: action.payload };
		case "SET_VIEWING_DELETED":
			return { ...state, viewingDeleted: action.payload };
		case "SET_FILTERS":
			return { ...state, filters: action.payload };
		case "SET_FILTER_OPTIONS":
			return { ...state, filterOptions: action.payload };
		case "SET_SELECTED_ASSET_ID":
			return { ...state, selectedAssetId: action.payload };
		case "SET_SMART_FILTERS":
			return { ...state, smartFilters: action.payload };
		default:
			return baseTableReducer(state, action);
	}
}

const AssetsTable: FC<AssetsTableProps> = (props: AssetsTableProps) => {
	const { browserLocationId } = props;
	const location = useLocation();
	const navigate = useNavigate();
	const user = useUserStore((state) => state.user);
	const [showImportModal, setShowImportModal] = useState(false);

	// Check if this is org 28 (they use different form IDs)
	const isOrg28 = user?.organization?.id === 28;

	// Find the organization form ID for the main asset capture form
	// Org 28 uses form ID 2 (Handyman Checklist), others use form ID 3 (Inventory & Tagging)
	const mainFormTemplateId = isOrg28 ? 2 : 3;
	const inventoryFormId = user?.organization?.forms?.find(
		(form: any) => (form.formId || form.form_id) === mainFormTemplateId,
	)?.id;

	const preservedState = location?.state?.assetsTableParams
		? location.state.assetsTableParams
		: {};

	const organization = user?.organization?.external_firebase_id;
	const [state, dispatch] = useReducer(reducer, {
		...initialState,
		pageNum: preservedState.pageNum || initialState.pageNum,
		pageSize: preservedState.pageSize || initialState.pageSize,
		searchVal: preservedState.searchVal || initialState.searchVal,
		sortBy: preservedState.sortBy || initialState.sortBy,
		sortOrder:
			(preservedState.sortOrder as SortOrder) || initialState.sortOrder,
		dateRange: preservedState.dateRange || initialState.dateRange,
		filters: preservedState.filters || initialState.filters,
		filterOptions: preservedState.filterOptions || initialState.filterOptions,
	});

	// Container ref
	const tableContainerRef = useRef<HTMLDivElement>(null);
	const [tableScrollY, setTableScrollY] = useState<number>(400);

	useEffect(() => {
		const calculateScrollY = () => {
			if (tableContainerRef.current) {
				const containerTop = tableContainerRef.current.getBoundingClientRect().top;
				// Subtract: toolbar+divider (~52px) + thead (~30px) + sticky pagination bar (~36px) + footer (~44px)
				const offset = 162;
				const available = window.innerHeight - containerTop - offset;
				setTableScrollY(Math.max(available, 200));
			}
		};
		const timer = setTimeout(calculateScrollY, 50);
		window.addEventListener("resize", calculateScrollY);
		return () => {
			clearTimeout(timer);
			window.removeEventListener("resize", calculateScrollY);
		};
	}, []);

	// Ref to track the latest viewingDeleted value to avoid stale closures
	const viewingDeletedRef = useRef(state.viewingDeleted);
	useEffect(() => {
		viewingDeletedRef.current = state.viewingDeleted;
	}, [state.viewingDeleted]);

	// Ref to track if we're in the middle of a viewingDeleted toggle
	const isTogglingDeletedRef = useRef(false);

	const {
		sortBy,
		sortOrder,
		pageNum,
		pageSize,
		searchVal,
		isLoading,
		columnsShown,
		dateRange,
		filters,
		filterOptions,
		tableColumns,
		selectedAssetId,
		smartFilters,
	} = state;

	// Define filterable columns for assets
	const filterableColumns: FilterableColumn[] = useMemo(
		() => [
			{ key: "assetType.name", label: "Asset Type", dataIndex: ["assetType", "name"], type: "text" },
			{ key: "manufacturer.name", label: "Manufacturer", dataIndex: ["manufacturer", "name"], type: "text" },
			{ key: "location.name", label: "Location", dataIndex: ["location", "name"], type: "text" },
			{ key: "location.city", label: "City", dataIndex: ["location", "city"], type: "text" },
			{ key: "location.state", label: "State", dataIndex: ["location", "state"], type: "text" },
			{ key: "condition", label: "Condition", dataIndex: "condition", type: "text" },
			{ key: "modelNumber", label: "Model", dataIndex: "modelNumber", type: "text" },
		],
		[],
	);

	// Handle smart filter changes - update state and session storage
	const handleSmartFiltersChange = useCallback((newFilters: FilterCriteria[]) => {
		dispatch({ type: "SET_SMART_FILTERS", payload: newFilters });
		setSessionValue(SESSION_KEYS.ASSET_SMART_FILTERS, newFilters);
	}, []);

	// Ref to track if filter options have been fetched
	const filterOptionsFetchedRef = useRef(false);

	const getFilterOptions = async (dispatch: React.Dispatch<Action>) => {
		// Include all asset types in filters (including deprecated) so users can filter existing assets
		const assetTypes = await selfOrganizationAssetTypes(true);
		dispatch({ type: "SET_FILTER_OPTIONS", payload: { assetTypes } });
	};

	// get filter options if no preserved state
	useEffect(() => {
		if (!filterOptions && !filterOptionsFetchedRef.current && organization) {
			filterOptionsFetchedRef.current = true;
			getFilterOptions(dispatch);
		}
	}, [organization, filterOptions]);

	// Check if we should open an asset detail view from navigation state
	useEffect(() => {
		if (location?.state?.selectedAssetId && !selectedAssetId) {
			dispatch({
				type: "SET_SELECTED_ASSET_ID",
				payload: location.state.selectedAssetId,
			});
		}
	}, [location?.state?.selectedAssetId, selectedAssetId]);

	// initialize columns after options fetched
	useEffect(() => {
		if (filterOptions !== null && organization) {
			initializeColumns(state, dispatch, organization);
		}
	}, [filterOptions, organization]);

	// Ref to track the last fetch key to prevent duplicate fetches
	const lastFetchKeyRef = useRef<string>("");

	// Single useEffect that handles all data fetching
	// viewingDeleted is included so filtering happens when toggled
	useEffect(() => {
		// Skip if we're in the middle of a viewingDeleted toggle (handled manually)
		if (isTogglingDeletedRef.current) {
			return;
		}

		// Create a fetch key from the current state to detect actual changes
		const fetchKey = JSON.stringify({
			pageNum,
			pageSize,
			sortBy,
			sortOrder,
			searchVal,
			dateRange,
			organization,
			filters,
			viewingDeleted: state.viewingDeleted,
			smartFilters,
		});

		// Skip if this is the exact same fetch we just did
		if (lastFetchKeyRef.current === fetchKey) {
			return;
		}

		// Update ref
		lastFetchKeyRef.current = fetchKey;

		// Use the ref to ensure we always have the latest viewingDeleted value
		fetchData(
			{ ...state, viewingDeleted: viewingDeletedRef.current },
			dispatch,
			browserLocationId,
		);
	}, [
		pageNum,
		pageSize,
		sortBy,
		sortOrder,
		searchVal,
		dateRange,
		organization,
		filters,
		state.viewingDeleted, // Keep in dependencies to update the ref
		smartFilters,
	]);

	const showAssetDetail = useCallback(
		(assetId: string) => {
			if (organization === DB_ORG.CUSHMANWAKEFIELD) {
				return;
			}
			dispatch({ type: "SET_SELECTED_ASSET_ID", payload: assetId });
		},
		[organization],
	);

	const handleRowSelection = {
		selectedRowKeys: state.checkedItems,
		onChange: (selectedRowKeys: React.Key[], selectedRows: Asset[]) => {
			const assetIdArr = selectedRows.map((row) => row.id);
			dispatch({ type: "SET_CHECKED_ITEMS", payload: assetIdArr });
		},
	};

	// ── Child asset tree expansion ──
	// Server-fetched rows carry childCount and an empty children placeholder;
	// the actual child rows are fetched on first expand and grafted into the
	// data source. Client-cached (org-wide) rows already have children nested.
	const graftChildren = useCallback(
		(rows: any[], parentId: number, children: any[]): any[] =>
			rows.map((row) => {
				if (row.id === parentId) return { ...row, children };
				if (row.children?.length) {
					return {
						...row,
						children: graftChildren(row.children, parentId, children),
					};
				}
				return row;
			}),
		[],
	);

	const handleRowExpand = useCallback(
		async (expanded: boolean, record: any) => {
			const needsFetch =
				expanded &&
				Array.isArray(record.children) &&
				record.children.length === 0 &&
				(record.childCount ?? 0) > 0;
			if (!needsFetch) return;

			try {
				const children = await getAssetChildren(record.id);
				const mapped = children.map((child: ChildAssetRow) => ({
					...child,
					// Reshape flat child rows into the nested objects the table
					// columns expect; children always share the parent's location.
					assetType: {
						id: child.organizationAssetTypeId,
						name: child.assetTypeName || "",
					},
					manufacturer: { name: child.manufacturerName || "" },
					location: record.location,
					children: child.childCount > 0 ? [] : undefined,
				}));
				dispatch({
					type: "SET_DATA_SOURCE",
					payload: graftChildren(state.dataSource as any[], record.id, mapped),
				});
			} catch (error) {
				console.error("Failed to load child assets:", error);
			}
		},
		[state.dataSource, graftChildren],
	);

	// Add sort order indicators to columns for controlled sorting
	// This ensures the correct sort arrow appears on the active column header
	const columnsWithSortOrder = addSortOrderToColumns(
		tableColumns,
		sortBy,
		sortOrder,
	);

	const tableProps = {
		dataSource: state.dataSource as Asset[],
		columns: columnsWithSortOrder,
		loading: isLoading,
		rowSelection: handleRowSelection,
		pagination: false as const,
		onRow: (record: Asset) => ({
			onClick: () => showAssetDetail(record.id.toString()),
			style: { cursor: "pointer" },
		}),
		rowKey: "id",
		scroll: { x: 1200, y: tableScrollY },
		expandable: { onExpand: handleRowExpand },
		// Use the unified table change handler for consistent sorting behavior
		onChange: createTableChangeHandler({ pageNum, pageSize }, dispatch),
	};

	const handleViewDeletedChange = useCallback(
		(checked: boolean) => {
			// Set flag to prevent pageNum effect from interfering
			isTogglingDeletedRef.current = true;

			// Update viewingDeleted state
			dispatch({ type: "SET_VIEWING_DELETED", payload: checked });

			// Reset pageNum to 1 immediately (before fetching) to avoid pagination issues
			if (state.pageNum !== 1) {
				dispatch({ type: "SET_PAGE_NUM", payload: 1 });
			}

			// Immediately call fetchData with the new value to ensure instant filtering
			// This avoids any race conditions with useEffect
			fetchData(
				{ ...state, viewingDeleted: checked, pageNum: 1 },
				dispatch,
				browserLocationId,
			);

			// Clear flag after a brief delay to allow state updates to settle
			setTimeout(() => {
				isTogglingDeletedRef.current = false;
			}, 100);
		},
		[state, browserLocationId],
	);

	const toggleDeleteModal = () => {
		dispatch({
			type: "SET_SHOW_DELETE_MODAL",
			payload: !state.showDeleteModal,
		});
	};

	const toggleSCExport = () => {
		dispatch({ type: "SET_SHOW_SC_EXPORT", payload: !state.showSCExport });
	};

	const handleRefresh = useCallback(() => {
		// Clear both the component's fetch key and the fetchData internal cache
		lastFetchKeyRef.current = "";
		clearAssetCache();
		fetchData(state, dispatch, browserLocationId);
	}, [state, browserLocationId]);

	// If an asset is selected, show the asset detail view
	if (selectedAssetId) {
		return (
			<AdminAssetDetail
				assetId={selectedAssetId}
				onBack={() =>
					dispatch({ type: "SET_SELECTED_ASSET_ID", payload: null })
				}
				onNavigateToAsset={(assetId: string) =>
					dispatch({ type: "SET_SELECTED_ASSET_ID", payload: assetId })
				}
			/>
		);
	}

	return (
		<div className="assets-page-container" ref={tableContainerRef}>
			<div className="table-header d-flex justify-content-between">
				<SmartFilter
					columns={filterableColumns}
					filters={smartFilters}
					onFiltersChange={handleSmartFiltersChange}
					data={state.dataSource as any[]}
					loading={isLoading}
					showIntegratedSearch={true}
					searchValue={searchVal}
					onSearchChange={(value: string) => {
						dispatch({ type: "SET_SEARCH_VAL", payload: value });
					}}
				/>

				<Space>
					<Button
						icon={<ReloadOutlined />}
						onClick={handleRefresh}
						loading={isLoading}
					>
						Refresh
					</Button>
					<Button
						icon={<UploadOutlined />}
						onClick={() => setShowImportModal(true)}
					>
						Import
					</Button>
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={() => {
							if (inventoryFormId) {
								const navigationState = browserLocationId
									? {
											locationId: browserLocationId,
										}
									: undefined;
								navigate(`/forms/${inventoryFormId}`, {
									state: navigationState,
								});
							} else {
								navigate("/forms");
							}
						}}
					>
						Capture an Asset
					</Button>
					<DeletedSwitch
						isLoading={isLoading}
						viewingDeleted={state.viewingDeleted}
						onViewDeletedChange={handleViewDeletedChange}
					/>
					<Tooltip title="Export">
						<Button
							icon={<DownloadOutlined style={{ fontSize: "20px" }} />}
							type="text"
							onClick={toggleSCExport}
						/>
					</Tooltip>
					<DeleteButton
						checkedItems={state.checkedItems || []}
						viewingDeleted={state.viewingDeleted}
						onClick={toggleDeleteModal}
					/>
				</Space>
			</div>
			<Divider />
			<Table
				locale={{
					emptyText: state.isLoading ? null : (
						<DFPEmpty
							className="w-100 h-100"
							description="No assets returned. Please modify your search term or update your filters and try again."
						/>
					),
				}}
				{...tableProps}
			/>

			{/* Sticky pagination bar */}
			<div className="assets-pagination-bar">
				<span className="pagination-total">
					{state.count > 0
						? `${(pageNum - 1) * pageSize + 1}-${Math.min(pageNum * pageSize, state.count)} of ${state.count} items`
						: "0 items"}
				</span>
				<Pagination
					current={pageNum}
					pageSize={pageSize}
					total={state.count}
					showSizeChanger
					pageSizeOptions={["25", "50", "100"]}
					size="small"
					onChange={(page, newPageSize) => {
						if (newPageSize !== pageSize) {
							dispatch({ type: "SET_PAGE_SIZE", payload: newPageSize });
							dispatch({ type: "SET_PAGE_NUM", payload: 1 });
						} else {
							dispatch({ type: "SET_PAGE_NUM", payload: page });
						}
					}}
				/>
			</div>

			<SCExportDialog
				state={state}
				dispatch={dispatch}
				browserLocationId={browserLocationId}
			/>
			<DeleteModal
				state={state}
				dispatch={dispatch}
				browserLocationId={browserLocationId}
				tableType="assets"
			/>
			<ImportAssetsModal
				open={showImportModal}
				onClose={() => setShowImportModal(false)}
				onImportComplete={() => {
					handleRefresh();
				}}
			/>
		</div>
	);
};

export { AssetsTable };
